import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Marker, Popup } from "react-leaflet";

// Actions
import {
  selectPoi,
  selectTargetGroup,
  unselectPoi,
  unselectTargetGroup,
} from "../../../actions/map-view/MapViewActions";
import { getMapState } from "../../../actions/map-state/MapStateActions";
import { getRegionDataByCity } from "../../../../actions/regions/RegionActions";
import {
  selectDataLayer,
  unselectDataLayer,
} from "../../../../actions/map-view/DataLayerSelectionActions";

// Constants and Utils
import {
  useSetPoiBrands,
  useSetTgSelected,
  useGetDataLayersDetails,
} from "../../../utils/HooksUtil";
import {
  useGetPoiHierarchy,
  useGetTgHeatMap,
} from "../../../../mavin/utils/hooks/HooksUtil";
import {
  constructPoiMarkers,
  MediaMarkers,
  NoMapViewFilterAppliedMessage,
  RoadStretchLines,
  updateSelectedFilterMap,
} from "./MapViewUtil";
import { MapZoom } from "../../../../constants/GeneralConstants";
import { getMapViewData } from "./MapViewUtils";
import { MapViewSelectedFilterKeys } from "./MapViewConstants";
import { constructZoneChipsData } from "../FiltersAndSelectionChipsCard";
import { downloadDomToImage } from "../../../../common-utils/image-utils/ImageUtils";

// Components
import CardCollapsible from "../../../../components/card-collapsible/CardCollapsible";
import CheckBox from "../../../../components/check-box/CheckBox";
import LLMap from "../../../../components/map/leaflet-map/LLMap";
import MapIcon from "../../../../components/map/map-icon/MapIcon";
import TargetGroupHeatmap from "../../../../components/map/target-group-heatmap/TargetGroupHeatmap";
import AlertMessage from "../../../../mavin/components/alert-message/AlertMessage";
import DataLayerMarkers from "../../../../pages/map-view/data-layers/DataLayerMarkers";

// Css
import "./MapView.scss";

// Selected Point of Interest Markers..
function PoiMarkers() {
  // poiTypeIdToSectorId
  const poiTypeIdToSectorId = useSelector(
    (state) => state.poi.poiTypeIdToSectorId
  );

  // brand data markers
  const brandDataMarkers = useSelector((state) =>
    Object.keys(state.proohMapView.poiSelectionMap).reduce((acc, eachPoiId) => {
      if (!state.proohMapView.poiSelectionMap[eachPoiId]) {
        return acc;
      }
      const eachBrandDataArray = state.proohMapView.poiToBrandData[eachPoiId];
      eachBrandDataArray.forEach((eachBrandData) => {
        acc = acc.concat(
          constructPoiMarkers(eachBrandData, poiTypeIdToSectorId)
        );
      });
      return acc;
    }, [])
  );

  if (brandDataMarkers.length <= 0) {
    return null;
  }

  return (
    <>
      {brandDataMarkers.map((marker, i) => {
        // poiType based icon (remove when not needed)
        // const icon = new MapIcon({ type: marker.type });

        // sector based icon
        const iconBasedOnSector = new MapIcon({ type: marker.sectorId });

        return (
          <Marker
            key={i}
            position={marker.position}
            // icon={icon}
            icon={iconBasedOnSector}
          >
            <Popup>
              <b>{marker.markerText}</b>
            </Popup>
          </Marker>
        );
      })}
    </>
  );
}

/**
 * Target Group Heat Map
 */
function TgHeatMap({ targetGroupId }) {
  const allPoiPointsArr = useSelector((state) => state.geoData.allPoiPointsArr);

  // checking tg is selected from map legend
  const isTgSelected =
    useSelector((state) => state.proohMapView.targetGroups[targetGroupId]) ||
    false;

  // if there are no points
  if (allPoiPointsArr.length < 1 || !isTgSelected) {
    return <TargetGroupHeatmap allPoiPointsArr={[]} hmkey="un-select" />;
  }

  return (
    <TargetGroupHeatmap
      hmkey={targetGroupId}
      allPoiPointsArr={allPoiPointsArr}
    />
  );
}

function ZonePanel({
  zones = [],
  selectedFiltersMap = {},
  setSelectedFiltersMap = () => {},
}) {
  const isFilterExist = zones.length > 0;
  const { selectedSubZoneIds = [] } = selectedFiltersMap || {};
  const constructedZones = constructZoneChipsData(zones);

  // function
  function handleCheckbox(e, subZoneId) {
    updateSelectedFilterMap(
      e,
      subZoneId,
      [MapViewSelectedFilterKeys.selectedSubZoneIds],
      selectedFiltersMap,
      setSelectedFiltersMap
    );
  }

  return (
    <CardCollapsible id="zone" header="Zone">
      {!isFilterExist && <NoMapViewFilterAppliedMessage />}
      {isFilterExist &&
        constructedZones.map((eachSubZone) => {
          const { id, label } = eachSubZone || {};
          return (
            <CheckBox
              key={id}
              checkBoxKey={label}
              boxStyle="mb-0 d-flex align-items-center"
              inputBoxStyle="mt-0"
              labelStyle="text-truncate"
              checked={selectedSubZoneIds.includes(id)}
              onChange={(e) => handleCheckbox(e, id)}
            />
          );
        })}
    </CardCollapsible>
  );
}

function MediaTypePanel({
  mediaTypes = [],
  selectedFiltersMap = {},
  setSelectedFiltersMap = () => {},
}) {
  const isFilterExist = mediaTypes.length > 0;
  const { selectedMediaTypes = [] } = selectedFiltersMap || {};

  // function
  function handleCheckbox(e, mediaType) {
    updateSelectedFilterMap(
      e,
      mediaType,
      [MapViewSelectedFilterKeys.selectedMediaTypes],
      selectedFiltersMap,
      setSelectedFiltersMap
    );
  }

  return (
    <CardCollapsible id="MediaType" header="Media Type">
      {!isFilterExist && <NoMapViewFilterAppliedMessage />}
      {isFilterExist &&
        mediaTypes.map((mediaType, i) => (
          <CheckBox
            key={i}
            checkBoxKey={mediaType}
            boxStyle="mb-0 d-flex align-items-center"
            inputBoxStyle="mt-0"
            labelStyle="text-truncate"
            checked={selectedMediaTypes.includes(mediaType)}
            onChange={(e) => handleCheckbox(e, mediaType)}
          />
        ))}
    </CardCollapsible>
  );
}

function ArterialRoutePanel({
  routeTypes = [],
  selectedFiltersMap = {},
  setSelectedFiltersMap = () => {},
}) {
  const isFilterExist = routeTypes.length > 0;
  const { selectedRouteTypes = [] } = selectedFiltersMap || {};

  // function
  function handleCheckbox(e, routeType) {
    updateSelectedFilterMap(
      e,
      routeType,
      [MapViewSelectedFilterKeys.selectedRouteTypes],
      selectedFiltersMap,
      setSelectedFiltersMap
    );
  }

  return (
    <CardCollapsible id="ArterialRoute" header="Arterial Route">
      {!isFilterExist && <NoMapViewFilterAppliedMessage />}
      {isFilterExist &&
        routeTypes.map((routeType, i) => (
          <CheckBox
            key={i}
            checkBoxKey={routeType}
            boxStyle="mb-0 d-flex align-items-center"
            inputBoxStyle="mt-0"
            labelStyle="text-truncate"
            checked={selectedRouteTypes.includes(routeType)}
            onChange={(e) => handleCheckbox(e, routeType)}
          />
        ))}
    </CardCollapsible>
  );
}

function PoiPanel() {
  const dispatch = useDispatch();

  // Selector
  const poiSelectionMap = useSelector(
    (state) => state.proohMapView.poiSelectionMap
  );
  const poiToBrandData = useSelector(
    (state) => state.proohMapView.poiToBrandData
  );

  // Get campaignPlan
  const cityCampaignPlan =
    useSelector((state) => state.campaignPlan.campaignPlan) || {};
  const { poiTypes = {} } = cityCampaignPlan;

  function constructDisplayValue(poiId) {
    const poiName = poiTypes[poiId];
    const count = poiToBrandData[poiId].reduce((acc, eachArray) => {
      acc = acc + eachArray.length;
      return acc;
    }, 0);
    return `${poiName}(${count})`;
  }

  function onSelectAction(e, selectionType) {
    const selectionTypeValue = selectionType.split("(")[0];
    const poiId = Object.entries(poiTypes).find(
      ([_v, value]) => value === selectionTypeValue
    )?.[0];

    const checked = e.target.checked;
    checked ? dispatch(selectPoi(poiId)) : dispatch(unselectPoi(poiId));
  }

  const isPoiToBrandDataExist = Object.keys(poiToBrandData).length > 0;

  return (
    <CardCollapsible id="poi" header={"Point of interest"}>
      {!isPoiToBrandDataExist && <NoMapViewFilterAppliedMessage />}
      {isPoiToBrandDataExist &&
        Object.keys(poiSelectionMap).length > 0 &&
        Object.keys(poiSelectionMap).map((poiId) => (
          <CheckBox
            key={poiId}
            checkBoxKey={constructDisplayValue(poiId)}
            boxStyle="mb-0 d-flex align-items-center"
            inputBoxStyle="mt-0"
            labelStyle="text-truncate"
            checked={poiSelectionMap[poiId] === true}
            onChange={onSelectAction}
          />
        ))}
    </CardCollapsible>
  );
}

function TargetGroupPanel({ targetGroupId = "" }) {
  const dispatch = useDispatch();

  const isTgSelected =
    useSelector((state) => state.proohMapView.targetGroups[targetGroupId]) ||
    false;

  function onSelectFunction(e) {
    const checked = e.target.checked;
    checked
      ? dispatch(selectTargetGroup(targetGroupId))
      : dispatch(unselectTargetGroup(targetGroupId));
  }

  const isPlanTgPresent = targetGroupId ? true : false;

  return (
    <CardCollapsible id="tg" header={"Target Group"}>
      {!isPlanTgPresent && <NoMapViewFilterAppliedMessage />}
      {isPlanTgPresent && (
        <CheckBox
          checkBoxKey={"Target Group Heatmap"}
          boxStyle="mb-0 d-flex align-items-center"
          inputBoxStyle="mt-0"
          labelStyle="text-truncate"
          onChange={onSelectFunction}
          checked={isTgSelected}
        />
      )}
    </CardCollapsible>
  );
}

function DataLayersPanel({ dataLayerIds = [], cityId }) {
  const dispatch = useDispatch();

  // data layers details map
  const dataLayerDetailsMap = useSelector(
    (state) => state.orgDataLayers.dataLayerDetailsMap
  );

  // Selector
  const dataLayerSelectedMap = useSelector(
    (state) => state.dataLayersSelection.isLayerSelected
  );

  const isPlanDataLayersPresent = dataLayerIds.length > 0;

  // select all ids initially
  useEffect(() => {
    dataLayerIds.forEach((id) => dispatch(selectDataLayer(id)));
  }, [dispatch, cityId, dataLayerIds.length]);

  // function
  function handleCheckboxChange(id, isLayerSelected) {
    // select and unSelect
    const dispatchFunction = isLayerSelected
      ? unselectDataLayer
      : selectDataLayer;

    dispatch(dispatchFunction(id));
  }

  return (
    <CardCollapsible id="dataLayers" header="Data Layers">
      {!isPlanDataLayersPresent && <NoMapViewFilterAppliedMessage />}
      {isPlanDataLayersPresent &&
        dataLayerIds.map((id, i) => {
          const { name, dataPointMap = {} } = dataLayerDetailsMap[id] || {};
          const isLayerSelected = dataLayerSelectedMap[id] || false;
          const cityDataPoints = dataPointMap?.[cityId] || [];
          const dataPointsCount = cityDataPoints.length;

          return (
            <CheckBox
              key={i}
              checkBoxKey={`${name} (${dataPointsCount})`}
              boxStyle="mb-0 d-flex align-items-center"
              inputBoxStyle="mt-0"
              labelStyle="text-truncate"
              checked={isLayerSelected}
              onChange={() => handleCheckboxChange(id, isLayerSelected)}
            />
          );
        })}
    </CardCollapsible>
  );
}

// Download Map Image
function DownloadMapImage() {
  // Campaign
  const campaign = useSelector((state) => state.c_campaignBasicInfo.campaign);
  const { title: campaignTitle } = campaign || {};

  // filename
  const imageFileName = `${campaignTitle} MapView.png`;

  // functions
  async function handleDownloadMapImage() {
    const input = document.getElementById("review-map");
    await downloadDomToImage(input, imageFileName);
  }

  return (
    <div className="text-center border-top download-map-btn">
      <button
        className="btn btn-primary py-1"
        onClick={handleDownloadMapImage}
        data-html2canvas-ignore="true"
      >
        Download Map Image
      </button>
    </div>
  );
}

/**
 * MapView
 */
export default function MapView({ campaignPlan = {}, roadStretchMap = {} }) {
  const dispatch = useDispatch();
  const mapRef = useRef();
  const { cityId, campaignId } = useParams();

  // State
  const [selectedFiltersMap, setSelectedFiltersMap] = useState({
    [MapViewSelectedFilterKeys.selectedSubZoneIds]: [],
    [MapViewSelectedFilterKeys.selectedRouteTypes]: [],
    [MapViewSelectedFilterKeys.selectedMediaTypes]: [],
  });

  // Campaign Medias
  const campaignMedias = useSelector(
    (state) => state.campaignMedia.campaignMedia
  );

  // mapState
  const mapState = useSelector((state) => state.proohMapState.mapState);
  const { poiBrandIds = {} } = mapState;

  // Selected Stretches in Planning
  const {
    targetGroupId,
    zones = [],
    mediaTypes = [],
    routeTypes = [],
    dataLayerIds = [],
  } = campaignPlan || {};

  // Info to get the tgHeatMap
  const bbox = useSelector((state) => state.region.regionData?.bbox) || "";
  const tgInfo = useSelector((state) => state.orgTargetGroup.tgInfo);
  const { targetGroup } = tgInfo || {};
  const { resultLayers = [] } = targetGroup || {};
  const resPoiLayers = resultLayers.map((eachLayer) => eachLayer.poiTypeId);

  // Loadings
  const mapStateLoading = useSelector(
    (state) => state.proohMapState.mapStateLoading
  );
  const regionDataLoading = useSelector(
    (state) => state.region.regionDataLoading
  );
  const poiAndBrandInfoLoading = useSelector(
    (state) => state.proohMapView.poiToBrandDataLoading
  );
  const heatMapDataLoading = useSelector(
    (state) => state.geoData.poiTypeLayerDataLoading
  );
  const poiLoading = useSelector((state) => state.poi.getPoiLoading);

  const isMapViewLoading =
    mapStateLoading ||
    regionDataLoading ||
    poiAndBrandInfoLoading ||
    heatMapDataLoading ||
    poiLoading;

  useEffect(() => {
    dispatch(getMapState(campaignId, cityId));
    dispatch(getRegionDataByCity(cityId));
  }, [dispatch, campaignId, cityId]);

  // Poi
  useSetPoiBrands(poiBrandIds, cityId, bbox);
  useGetPoiHierarchy(cityId);

  // tg heatmap
  useGetTgHeatMap(resPoiLayers, bbox);
  useSetTgSelected(targetGroupId);

  // Data Layers
  useGetDataLayersDetails(dataLayerIds);

  // useEffect to updated "appliedFilters" to "selectedFiltersMap" for mapView
  useEffect(() => {
    setSelectedFiltersMap({
      [MapViewSelectedFilterKeys.selectedSubZoneIds]: zones.map(
        (eachSubZone) => eachSubZone.zoneId
      ),
      [MapViewSelectedFilterKeys.selectedRouteTypes]: routeTypes,
      [MapViewSelectedFilterKeys.selectedMediaTypes]: mediaTypes,
    });
  }, [JSON.stringify(campaignPlan)]);

  // Loading
  if (isMapViewLoading) {
    return (
      <AlertMessage
        className="justify-content-center p-3 my-4"
        textMessage="Map View Loading, Please wait.."
      />
    );
  }

  // no data case
  if (!campaignPlan || Object.keys(roadStretchMap).length < 1) {
    return null;
  }

  // Map Coordinates
  const center = Object.values(roadStretchMap)[0].trace[0];

  // Filtered Media & Stretches after Show/Hide From Map
  const mapViewData = getMapViewData(
    selectedFiltersMap,
    campaignMedias,
    campaignPlan,
    roadStretchMap
  );
  const { filteredStretchesMap, filteredMediasMap } = mapViewData || {};

  return (
    <div className="mt-5">
      <h4> MapView - (zoom on map for detailed view)</h4>
      <div className="cont-map-view bg-alt">
        {/* Legends for mapView */}
        <div className="map-controls-cont overflow-auto">
          <div className="position-relative h-100 overflow-auto">
            <h5 className="header">Map Display Settings</h5>
            <ZonePanel
              zones={zones}
              selectedFiltersMap={selectedFiltersMap}
              setSelectedFiltersMap={setSelectedFiltersMap}
            />
            <MediaTypePanel
              mediaTypes={mediaTypes}
              selectedFiltersMap={selectedFiltersMap}
              setSelectedFiltersMap={setSelectedFiltersMap}
            />
            <ArterialRoutePanel
              routeTypes={routeTypes}
              selectedFiltersMap={selectedFiltersMap}
              setSelectedFiltersMap={setSelectedFiltersMap}
            />
            <PoiPanel />
            <TargetGroupPanel targetGroupId={targetGroupId} />
            <DataLayersPanel dataLayerIds={dataLayerIds} cityId={cityId} />

            {/* Download Map Image */}
            <DownloadMapImage />
          </div>
        </div>

        {/* MapView */}
        <div className="h-100" ref={mapRef} id="review-map">
          <LLMap
            center={[center[0], center[1]]}
            zoom={MapZoom.zoomLevel11}
            scrollWheelZoom={false}
          >
            <RoadStretchLines filteredStretchesMap={filteredStretchesMap} />
            <MediaMarkers filteredMediasMap={filteredMediasMap} />
            <PoiMarkers />
            <TgHeatMap targetGroupId={targetGroupId} />
            <DataLayerMarkers cityId={cityId} />
          </LLMap>
        </div>
      </div>
    </div>
  );
}
